import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Breadcrumbs as MuiBreadcrumbs,
    Link,
    Stack,
    Typography,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';

// ─── Page actions context ───────────────────────────────────────────────────
// Allows individual pages to inject right-side action buttons into the global
// header without page-level layout changes.
const PageActionsContext = createContext({ setActions: () => {}, actions: null });

export const PageActionsProvider = ({ children }) => {
    const [actions, setActions] = useState(null);
    const value = useMemo(() => ({ actions, setActions }), [actions]);
    return <PageActionsContext.Provider value={value}>{children}</PageActionsContext.Provider>;
};

/**
 * usePageActions — set action node (buttons/menu) shown in the global PageHeader.
 * Pass `null` (or omit on unmount) to clear.
 *
 *   usePageActions(<Button onClick={...}>Create</Button>);
 */
export const usePageActions = (node) => {
    const { setActions } = useContext(PageActionsContext);
    useEffect(() => {
        setActions(node);
        return () => setActions(null);
        // node identity will change on every render — caller can wrap in useMemo if needed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node]);
};

// ─── Menu lookup ────────────────────────────────────────────────────────────
// Recursively flatten menu items into { url, title, breadcrumbPath } entries.
const flattenMenu = (items, breadcrumbPath = []) => {
    const out = [];
    if (!Array.isArray(items)) return out;
    for (const item of items) {
        if (!item) continue;
        const nextBreadcrumb = item.title ? [...breadcrumbPath, { title: item.title, url: item.url }] : breadcrumbPath;
        if (item.url) {
            out.push({ url: item.url, title: item.title, icon: item.icon, breadcrumbPath: nextBreadcrumb });
        }
        if (Array.isArray(item.children)) {
            out.push(...flattenMenu(item.children, nextBreadcrumb));
        }
    }
    return out;
};

const findBestMatch = (entries, pathname) => {
    let best = null;
    let bestLen = -1;
    for (const e of entries) {
        if (!e.url) continue;
        if (pathname === e.url || pathname.startsWith(e.url + '/')) {
            if (e.url.length > bestLen) {
                best = e;
                bestLen = e.url.length;
            }
        }
    }
    return best;
};

// ─── PageHeader visual ──────────────────────────────────────────────────────
const PageHeader = ({ navigation }) => {
    const theme = useTheme();
    const { pathname } = useLocation();
    const { actions } = useContext(PageActionsContext);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const entries = useMemo(() => flattenMenu(navigation?.items), [navigation]);
    const match = useMemo(() => findBestMatch(entries, pathname), [entries, pathname]);

    // Don't render on Dashboard root (already has its own header look)
    if (!match || pathname === '/' || pathname === '/dashboard/default') return null;

    const crumbs = match.breadcrumbPath || [];
    const Icon = match.icon;

    return (
        <Box
            sx={{
                width: '100%',
                mb: 2,
                px: 0.5
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={1.25}
            >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                    {Icon && (
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: theme.palette.primary.light,
                                color: theme.palette.primary.dark,
                                flexShrink: 0
                            }}
                        >
                            <Icon stroke={1.5} size="1.3rem" />
                        </Box>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="h3"
                            noWrap
                            sx={{ fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.2 }}
                        >
                            {match.title}
                        </Typography>
                        {!isMobile && crumbs.length > 0 && (
                            <MuiBreadcrumbs
                                separator={<NavigateNextIcon fontSize="small" />}
                                sx={{ mt: 0.25, '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}
                            >
                                <Link
                                    component={RouterLink}
                                    to="/"
                                    underline="hover"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        color: 'text.secondary',
                                        fontSize: '0.78rem'
                                    }}
                                >
                                    <HomeRoundedIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                    Home
                                </Link>
                                {crumbs.slice(0, -1).map((c, i) => (
                                    <Typography
                                        key={`${c.title}-${i}`}
                                        sx={{ color: 'text.secondary', fontSize: '0.78rem' }}
                                    >
                                        {c.title}
                                    </Typography>
                                ))}
                                {crumbs.length > 0 && (
                                    <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.78rem' }}>
                                        {crumbs[crumbs.length - 1].title}
                                    </Typography>
                                )}
                            </MuiBreadcrumbs>
                        )}
                    </Box>
                </Stack>

                {actions && (
                    <Box sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}>{actions}</Box>
                )}
            </Stack>
        </Box>
    );
};

export default PageHeader;
